import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { getAppointments } from "@/api/services";
import Header from "@/components/Header";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Loader2, Video, Phone, MessageSquare } from "lucide-react";
import { cn } from "@/utils/cn";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday } from "date-fns";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function CalendarPage() {
  const { astrologer } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (!astrologer?.astrologerId || !astrologer?.accessToken) return;

    setLoading(true);
    getAppointments(astrologer.astrologerId, astrologer.accessToken)
      .then(({ data }) => {
        if (data?.appointments) {
          setAppointments(data.appointments);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [astrologer]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPadding = monthStart.getDay();
  const paddedDays = [...Array(startPadding).fill(null), ...days];

  const getAppointmentsForDay = (date) => {
    if (!date) return [];
    return appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return isSameDay(aptDate, date);
    });
  };

  const selectedDayAppointments = getAppointmentsForDay(selectedDate);

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case "video": return Video;
      case "chat": return MessageSquare;
      default: return Phone;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-green-100 text-green-700";
      case "pending": return "bg-yellow-100 text-yellow-700";
      case "cancelled": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <>
      <Header name="My Calendar" />
      <div className="w-full p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Calendar */}
          <div className="lg:col-span-1 bg-white rounded-lg p-4 shadow-sm">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-1 hover:bg-gray-100 rounded">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <h3 className="font-semibold">{format(currentMonth, "MMMM yyyy")}</h3>
              <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-1 hover:bg-gray-100 rounded">
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {paddedDays.map((day, idx) => {
                if (!day) return <div key={`pad-${idx}`} className="aspect-square" />;
                
                const dayAppointments = getAppointmentsForDay(day);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const hasAppointments = dayAppointments.length > 0;

                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => setSelectedDate(day)}
                    className={cn(
                      "aspect-square flex flex-col items-center justify-center rounded-lg text-sm transition-colors relative",
                      !isSameMonth(day, currentMonth) && "text-gray-300",
                      isToday(day) && "ring-2 ring-orange-500",
                      isSelected && "bg-orange-500 text-white",
                      !isSelected && hasAppointments && "bg-orange-50",
                      !isSelected && !hasAppointments && "hover:bg-gray-100"
                    )}
                  >
                    {format(day, "d")}
                    {hasAppointments && (
                      <div className={cn(
                        "absolute bottom-1 w-1 h-1 rounded-full",
                        isSelected ? "bg-white" : "bg-orange-500"
                      )} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected Day Appointments */}
          <div className="lg:col-span-2 bg-white rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4">
              {isToday(selectedDate) ? "Today" : format(selectedDate, "EEEE, MMMM d, yyyy")}
            </h3>

            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-6 w-6 text-orange-500 animate-spin" />
              </div>
            ) : selectedDayAppointments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No appointments on this day</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDayAppointments.map((apt) => {
                  const ModeIcon = getModeIcon(apt.mode);
                  return (
                    <div
                      key={apt._id}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
                        <ModeIcon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {apt.user?.name || "Client"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatTime(apt.time)} · {apt.typeofmeeting || "Consultation"}
                        </p>
                      </div>
                      <span className={cn("text-xs px-2 py-1 rounded-full", getStatusColor(apt.status))}>
                        {apt.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
